import _ from 'lodash';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { FLOW_CONFIG, journeyCanvasConstants, stepItemConfig } from '../../../../constants/JourneyCanvas';
import { JourneyCanvasState, JourneyStepAction, NotifyDialogStatus, SidebarState } from '../../utility/TypeDefs';
import { calculateDuration, findEmptyPositionOnCanvas, generateNodeId } from '../../utility/helpers';
import { ClickStepCardPayload, ClickStepConnectorIconPayload, DeleteFlowItemPayload, SetJourneyStepDetailsPayload, SetStepCardMenuCoordinatesPayload } from '../TypeDefs';

import stepConnectorIcon from '../../../../assets/images/AddStepIcon.svg';
import menuDotsIcon from '../../../../assets/images/MenuDots.png';
import cemCountIcon from '../../../../assets/images/StepCemCountIcon.svg';
import eventCountIcon from '../../../../assets/images/StepEventCountIcon.svg';
import stepIcon from '../../../../assets/images/StepIcon.svg';
import stepIconWhite from '../../../../assets/images/StepIconWhite.svg';
import serviceCountIcon from '../../../../assets/images/StepServiceCountIcon.svg';

import { FlowConnector, FlowItem, StepItem } from '../../../../_components/flow-builder/TypeDefs';
import { findToDeleteFlowItems, noneSelected } from './FlowItemsReducerActions';

/**
 * @method clickStepCard
 * @description On click of step card we should go to the edit step canvas
 * @param state Journey canvas state
 * @returns showStepModal
 */
export const clickStepCard = (state: JourneyCanvasState, { nodeId }: ClickStepCardPayload): JourneyCanvasState => {
  const calculatedDuration = calculateDuration(state.stepJourneyDataMap[nodeId].maxExecutionTime);
  const { startingNode } = state.stepJourneyDataMap[nodeId];
  const items = _.cloneDeep(state.stepJourneyDataMap[nodeId].flowItems);
  const prevStepCardUUID = state.stepConnectors.find((sc) => sc.toNodeId === nodeId)?.fromNodeId ?? '';
  const { journeyStepAction } = state.stepJourneyDataMap[nodeId];
  return {
    ...state,
    designingStep: true,
    stepCardUUID: nodeId,
    journeyStepName: state.stepJourneyDataMap[nodeId].journeyStepName,
    flowItems: startingNode ? [{
      ...startingNode,
      config: {
        ...startingNode.config,
        orientation: Orientation.VERTICAL,
      },
      outgoingIcon: {
        src: stepIconWhite,
        height: journeyCanvasConstants.stepStartingItemIconSize,
        width: journeyCanvasConstants.stepStartingItemIconSize
      },
      isRoot: true
    }, ...items] : items,
    flowConnectors: _.cloneDeep(state.stepJourneyDataMap[nodeId].flowConnectors),
    ingressNodeId: state.stepJourneyDataMap[nodeId].ingressNodeId,
    designingInitialStepJourney: state.stepItems.findIndex((si) => si.nodeId === nodeId) === 0,
    maxExecutionTime: state.stepJourneyDataMap[nodeId].maxExecutionTime,
    maxExecutionTimeInput: {
      hours: calculatedDuration.hours.toString(),
      minutes: calculatedDuration.minutes.toString(),
      seconds: calculatedDuration.seconds.toString()
    },
    isRootExternal: state.stepJourneyDataMap[nodeId].isRootExternal,
    saveJourneyBtnEnabled: false,
    prevStepCardUUID,
    journeyStepAction,
    payloadTransformationsCopy: _.cloneDeep(state.payloadTransformations),
    flowItemDataMapCopy: _.cloneDeep(state.flowItemDataMap),
  };
};

/**
 * @method closeJourneyStepModal
 * @description set values for showUUIDModal, maxExecutionTime and uniqueIdentifiers
 * @param state Journey canvas state
 * @param save is save button clicked
 * @returns set showUUIDModal, maxExecutionTime and uniqueIdentifiers
 */
export const closeJourneyStepModal = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  showJourneyStepModal: false,
  uniqueIdentifiers: [],
  journeyStepName: '',
  journeyStepDescription: '',
  designingStep: false,
  prevStepCardUUID: '',
  journeyStepAction: JourneyStepAction.NONE
});

/**
 * @method clickStepConnectorIcon
 * @description On click of step connector icon we should set show Step modal to true
 * @param state Journey canvas state
 * @returns showStepModal
 */
export const clickStepConnectorIcon = (state: JourneyCanvasState, { prevNodeId }: ClickStepConnectorIconPayload): JourneyCanvasState => {
  const lastElementIdf = state.stepJourneyDataMap[prevNodeId].flowItems.filter((fi) => !state.stepJourneyDataMap[prevNodeId].flowConnectors.find((fc) => fc.fromNodeId === fi.nodeId));
  const dropdownValues: { value: string; name: string; }[] = [];
  lastElementIdf.forEach((e) => {
    dropdownValues.push({
      value: e.nodeId,
      name: e.name
    });
  });
  return {
    ...state,
    dropdownEndpointValues: [...dropdownValues],
    showJourneyStepModal: true,
    prevStepCardUUID: prevNodeId,
    maxExecutionTimeInput: { hours: '0', minutes: '0', seconds: '0' },
    selectedEndpointFlowItem: undefined,
    journeyStepName: '',
    journeyStepDescription: '',
    uniqueIdentifiers: undefined,
  };
};

/**
 * @method clickInitiateStepCard
 * @description On click of step initiate card we should set show Step modal to true
 * @param state Journey canvas state
 * @returns showStepModal
 */
export const clickInitiateStepCard = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  dropdownEndpointValues: [],
  showJourneyStepModal: true,
  prevStepCardUUID: '',
  flowItems: [],
  flowConnectors: [],
  designingInitialStepJourney: true,
  maxExecutionTimeInput: { hours: '0', minutes: '0', seconds: '0' },
  selectedEndpointFlowItem: undefined,
  journeyStepName: '',
  journeyStepDescription: '',
  uniqueIdentifiers: undefined,
  journeyStepAction: JourneyStepAction.CREATE
});

/**
 * @method setJourneyStepDetails
 * @description Sets the journey step details.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {SetJourneyStepDetailsPayload} payload - The payload containing the stepName, maxExcTime, uniqueIdentifiers, and journeyStepDescription.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const setJourneyStepDetails = (state: JourneyCanvasState, { stepName, maxExcTime, uniqueIdentifiers, journeyStepDescription, endpointNodeId, isEdit }: SetJourneyStepDetailsPayload): JourneyCanvasState => {
  // if editing step details just save the info nothing else
  if (isEdit) {
    if (!state.deletingNodeId) {
      return state;
    }
    // if already creating then action should still be create
    const journeyStepAction = state.stepJourneyDataMap[state.deletingNodeId].journeyStepAction === JourneyStepAction.CREATE ? JourneyStepAction.CREATE : JourneyStepAction.UPDATE;
    return {
      ...state,
      stepJourneyDataMap: {
        ...state.stepJourneyDataMap,
        [state.deletingNodeId]: {
          ...state.stepJourneyDataMap[state.deletingNodeId],
          journeyStepName: stepName,
          journeyStepDescription: journeyStepDescription ?? '',
          uniqueIdentifiers,
          maxExecutionTime: (Number(maxExcTime.hours) * 3600) + (Number(maxExcTime.minutes) * 60) + (Number(maxExcTime.seconds)),
          journeyStepAction,
        }
      },
      journeyStepName: stepName,
      maxExecutionTimeInput: maxExcTime,
      uniqueIdentifiers,
      journeyStepDescription: journeyStepDescription ?? '',
      showJourneyStepModal: false,
    };
  }

  const endpoint = state.stepJourneyDataMap[state.prevStepCardUUID]?.flowItems?.find((item) => item.nodeId === endpointNodeId);
  let newState: JourneyCanvasState;
  if (endpoint) {
    newState = {
      ...state,
      designingStep: true,
      flowItems: [{
        ...endpoint,
        config: {
          ...endpoint.config,
          orientation: Orientation.VERTICAL,
          draggable: false
        },
        x: journeyCanvasConstants.rootExternalItemPosition.x,
        y: journeyCanvasConstants.rootExternalItemPosition.y,
        buttons: [],
        isStackedStyled: false,
      }],
      flowConnectors: [],
      designingInitialStepJourney: false,
      showJourneyStepModal: false,
      saveJourneyBtnEnabled: false,
      journeyStepAction: JourneyStepAction.CREATE,
      ingressNodeId: '',
    };
  } else {
    newState = {
      ...state,
      showJourneyStepModal: false,
      startSelected: true,
      designingStep: true,
      sidebarState: SidebarState.ALL_SERVICES,
      saveJourneyBtnEnabled: false,
      journeyStepAction: JourneyStepAction.CREATE,
      ingressNodeId: '',
    };
  }

  return {
    ...newState,
    journeyStepName: stepName,
    maxExecutionTimeInput: maxExcTime,
    uniqueIdentifiers,
    journeyStepDescription: journeyStepDescription ?? '',
    selectedEndpointFlowItem: endpoint,
    flowItemDataMapCopy: _.cloneDeep(state.flowItemDataMap)
  };
};

/**
 * @method cancelStepDesign
 * @description Cancels the step design and resets the journey canvas state.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const cancelStepDesign = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  journeyStepName: '',
  journeyStepDescription: '',
  flowItems: [],
  flowConnectors: [],
  maxExecutionTimeInput: { hours: '', minutes: '', seconds: '' },
  maxExecutionTime: 0,
  uniqueIdentifiers: [],
  dropdownEndpointValues: [],
  selectedEndpointFlowItem: undefined,
  selectedFlowItem: undefined,
  filteredServices: undefined,
  searchTerm: '',
  sidebarState: SidebarState.NONE,
  startSelected: false,
  designingStep: false,
  saveJourneyBtnEnabled: false,
  stepCardUUID: '',
  prevStepCardUUID: '',
  payloadTransformations: state.payloadTransformationsCopy ?? [],
  payloadTransformationsCopy: undefined,
  flowItemDataMap: state.flowItemDataMapCopy ?? {},
  flowItemDataMapCopy: undefined,
  designingCEM: false,
  cemRootNodeId: undefined,
});

/**
* @method storeStepJourney
* @description Store the step journey as backup
* @param state Journey canvas state
*/
export const storeStepJourney = (state: JourneyCanvasState): JourneyCanvasState => {
  const stepJourneyNodeId = generateNodeId();
  const prevStepItem = state.stepItems.find((e) => e.nodeId === state.prevStepCardUUID);
  const { x, y } = findEmptyPositionOnCanvas(state.stepItems, true, prevStepItem, false, FLOW_CONFIG.orientation);
  const calculatedDuration = {
    hours: '0',
    minutes: '0',
    seconds: '0'
  };
  const duration = (Number(state.maxExecutionTimeInput.hours) * 3600) + (Number(state.maxExecutionTimeInput.minutes) * 60) + (Number(state.maxExecutionTimeInput.seconds));

  let { flowItems } = state;
  const { flowConnectors } = state;
  let startingNode: FlowItem | undefined;
  if (state.prevStepCardUUID || state.stepJourneyDataMap[state.stepCardUUID]?.startingNode) {
    flowItems = state.flowItems.slice(1);
    [startingNode] = state.flowItems;
  }
  const totalCEMsCount = flowItems.filter((fi) => !!state.flowItemDataMap[fi.nodeId]?.compensatoryEventMapName).length;
  const totalServicesCount = flowItems.length;
  const totalEventsCount = flowConnectors.length;

  if (state.stepCardUUID) {
    let endpointMismatch = false;
    const currentStepEdge = state.stepConnectors.find((c) => c.fromNodeId === state.stepCardUUID);
    if (currentStepEdge) {
      const lastElementIdf = state.flowItems.filter((fi) => !state.flowConnectors.find((fc) => fc.fromNodeId === fi.nodeId));
      const nextStepStartingNode = state.stepJourneyDataMap[currentStepEdge.toNodeId].startingNode;
      endpointMismatch = !lastElementIdf.find((node) => node.name === nextStepStartingNode?.name);
    }
    if (endpointMismatch) {
      return {
        ...state,
        deletingNodeId: currentStepEdge?.toNodeId,
        notifyDialogStatus: NotifyDialogStatus.DELETE_SUBSEQUENT_STEP,
        showNotifyDialog: true,
      };
    }

    const existingStepCard = state.stepItems.find((s) => s.nodeId === state.stepCardUUID);
    if (!existingStepCard) {
      return state;
    }
    existingStepCard.cemCount = totalCEMsCount;
    existingStepCard.eventCount = totalEventsCount;
    existingStepCard.serviceCount = totalServicesCount;

    return {
      ...state,
      stepJourneyDataMap: {
        ...state.stepJourneyDataMap,
        [state.stepCardUUID]: {
          ...state.stepJourneyDataMap[state.stepCardUUID],
          flowItems,
          flowConnectors: state.flowConnectors,
          ingressNodeId: state.ingressNodeId,
          isRootExternal: state.isRootExternal,
          startingNode,
          maxExecutionTime: duration,
          journeyStepAction: [JourneyStepAction.NONE, JourneyStepAction.UPDATE].includes(state.stepJourneyDataMap[state.stepCardUUID].journeyStepAction) ? JourneyStepAction.UPDATE : JourneyStepAction.CREATE
        }
      },
      stepItems: [...state.stepItems],
      updateVersion: false,
      saveJourneyBtnEnabled: false,
      enableActiveJourney: state.journeyStatus.toLowerCase() === 'draft',
      maxExecutionTimeInput: calculatedDuration,
      selectedFlowItem: undefined,
      filteredServices: undefined,
      searchTerm: '',
      sidebarState: SidebarState.NONE,
      startSelected: false,
      designingStep: false,
      initiateStepCardVisible: false,
      journeyStepName: '',
      journeyStepDescription: '',
      stepCardUUID: '',
      prevStepCardUUID: '',
      flowItems: [],
      flowConnectors: [],
      showNotifyDialog: false,
    };
  }
  return {
    ...state,
    stepJourneyDataMap: {
      ...state.stepJourneyDataMap,
      [stepJourneyNodeId]: {
        flowItems,
        flowConnectors: state.flowConnectors,
        ingressNodeId: state.ingressNodeId,
        isRootExternal: state.isRootExternal,
        journeyStepName: state.journeyStepName,
        journeyStepDescription: state.journeyStepDescription,
        uniqueIdentifiers: state.uniqueIdentifiers,
        maxExecutionTime: duration,
        journeyStepAction: JourneyStepAction.CREATE,
        startingNode,
      }
    },
    stepItems: [...state.stepItems,
    {
      nodeId: stepJourneyNodeId,
      x,
      y,
      name: state.journeyStepName,
      description: state.journeyStepDescription,
      serviceCount: totalServicesCount,
      eventCount: totalEventsCount,
      cemCount: totalCEMsCount,
      serviceCountIcon,
      eventCountIcon,
      cemCountIcon,
      menuDotsIcon,
      buttons: [],
      stepIcon,
      isRoot: false,
      isStackedStyled: false,
      stepConnectorIcon,
      config: stepItemConfig
    }],
    stepConnectors: [...state.stepConnectors,
    {
      fromNodeId: state.prevStepCardUUID,
      toNodeId: stepJourneyNodeId,
      orientation: { from: Orientation.HORIZONTAL, to: Orientation.HORIZONTAL },
      text: state.flowConnectors[0]?.text,
      displayEventVersion: state.flowConnectors[0]?.displayEventVersion,
      tooltip: state.flowConnectors[0]?.text,
      buttons: [],
      config: {},
    }],
    updateVersion: false,
    saveJourneyBtnEnabled: false,
    enableActiveJourney: state.journeyStatus.toLowerCase() === 'draft',
    maxExecutionTimeInput: calculatedDuration,
    uniqueIdentifiers: [],
    selectedFlowItem: undefined,
    filteredServices: undefined,
    searchTerm: '',
    sidebarState: SidebarState.NONE,
    startSelected: false,
    designingStep: false,
    initiateStepCardVisible: false,
    isRootExternal: false,
    journeyStepName: '',
    journeyStepDescription: '',
    prevStepCardUUID: '',
    flowItems: [],
    flowConnectors: [],
  };
};

/**
 * @method setStepCardMenuCoordinates
 * @description Sets the coordinates of the step card menu dropdown.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {SetStepCardMenuCoordinatesPayload} payload - The payload containing the mouseX, mouseY, and nodeId.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const setStepCardMenuCoordinates = (state: JourneyCanvasState, { mouseX, mouseY, nodeId }: SetStepCardMenuCoordinatesPayload) => ({
  ...state,
  deletingNodeId: nodeId,
  stepCardMenuDropdown: {
    ...state.stepCardMenuDropdown,
    isVisible: true,
    xCoordinate: mouseX,
    yCoordinate: mouseY
  },
});

/**
 * @method deleteStepItem
 * @description delete specific step item
 * @param state Journey canvas state
 * @param nodeId node id of flow item needs to be deleted
 * @returns reset values by calling noneSelected()
 */
export const deleteStepItem = (state: JourneyCanvasState, {
  nodeId
}: DeleteFlowItemPayload): JourneyCanvasState => {
  const toBeDeleted = findToDeleteFlowItems(
    state.stepItems,
    state.stepConnectors,
    nodeId
  );
  const deletedStepItems: StepItem[] = [];
  const deletedStepConnectors: FlowConnector[] = [];
  toBeDeleted.forEach((nodeId) => {
    if (state.stepJourneyDataMap[nodeId].journeyStepAction === JourneyStepAction.CREATE) {
      state.stepItems = state.stepItems.filter((f) => f.nodeId !== nodeId);
    } else {
      deletedStepItems.push(...state.stepItems.filter((f) => f.nodeId === nodeId));
      deletedStepConnectors.push(...state.stepConnectors.filter(
        (fc) =>
          toBeDeleted.includes(fc.fromNodeId)
          || toBeDeleted.includes(fc.toNodeId)
      ));
    }
    state.stepJourneyDataMap[nodeId].journeyStepAction = JourneyStepAction.DELETE;
  });
  const stepItems = state.stepItems.filter((f) => !toBeDeleted.includes(f.nodeId));
  const newState: JourneyCanvasState = {
    ...state,
    stepItems,
    deletedStepItems: [...deletedStepItems, ...state.deletedStepItems],
    stepConnectors: state.stepConnectors.filter(
      (fc) =>
        !toBeDeleted.includes(fc.fromNodeId)
        && !toBeDeleted.includes(fc.toNodeId)
    ),
    deletedStepConnectors: [...deletedStepConnectors, ...state.deletedStepConnectors],
    initiateStepCardVisible: stepItems.length === 0,
    isRootExternal: state.flowItems.length === 0 ? false : state.isRootExternal, // will show start button if canvas is completely empty
    showNotifyDialog: false,
    stepCardMenuDropdown: {
      ...state.stepCardMenuDropdown,
      xCoordinate: 0,
      yCoordinate: 0,
      isVisible: false
    }
  };

  // deselect everything
  return noneSelected(newState);
};

/**
 * @method dismissStepCardMenuDropdown
 * @description Dismisses the step card menu dropdown.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const dismissStepCardMenuDropdown = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  stepCardMenuDropdown: {
    ...state.stepCardMenuDropdown,
    isVisible: false
  }
});

/**
 * @method deleteSubsequentSteps
 * @description Deletes subsequent steps and updates the state of the journey canvas.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {DeleteFlowItemPayload} payload - The payload containing the nodeId.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const deleteSubsequentSteps = (state: JourneyCanvasState, {
  nodeId
}: DeleteFlowItemPayload): JourneyCanvasState => {
  const toBeDeleted = findToDeleteFlowItems(
    state.stepItems,
    state.stepConnectors,
    nodeId
  );
  const deletedStepItems: StepItem[] = [];
  const deletedStepConnectors: FlowConnector[] = [];
  toBeDeleted.forEach((nodeId) => {
    if (state.stepJourneyDataMap[nodeId].journeyStepAction === JourneyStepAction.CREATE) {
      state.stepItems = state.stepItems.filter((f) => f.nodeId !== nodeId);
    } else {
      deletedStepItems.push(...state.stepItems.filter((f) => f.nodeId === nodeId));
      deletedStepConnectors.push(...state.stepConnectors.filter(
        (fc) =>
          toBeDeleted.includes(fc.fromNodeId)
          || toBeDeleted.includes(fc.toNodeId)
      ));
    }
    state.stepJourneyDataMap[nodeId].journeyStepAction = JourneyStepAction.DELETE;
  });
  const stepItems = state.stepItems.filter((f) => !toBeDeleted.includes(f.nodeId));
  let { flowItems } = state;
  let startingNode: FlowItem | undefined;
  if (state.stepJourneyDataMap[state.stepCardUUID]?.startingNode) {
    flowItems = state.flowItems.slice(1);
    [startingNode] = state.flowItems;
  }

  const totalCEMsCount = flowItems.filter((fi) => !!state.flowItemDataMap[fi.nodeId]?.compensatoryEventMapName).length;
  const totalServicesCount = flowItems.length;
  const totalEventsCount = state.flowConnectors.length;

  const existingStepCard = state.stepItems.find((s) => s.nodeId === state.stepCardUUID);
  if (!existingStepCard) {
    return state;
  }
  existingStepCard.cemCount = totalCEMsCount;
  existingStepCard.eventCount = totalEventsCount;
  existingStepCard.serviceCount = totalServicesCount;

  const calculatedDuration = {
    hours: '0',
    minutes: '0',
    seconds: '0'
  };
  const duration = (Number(state.maxExecutionTimeInput.hours) * 3600) + (Number(state.maxExecutionTimeInput.minutes) * 60) + (Number(state.maxExecutionTimeInput.seconds));

  return {
    ...state,
    stepItems,
    deletedStepItems,
    deletedStepConnectors,
    stepConnectors: state.stepConnectors.filter(
      (fc) =>
        !toBeDeleted.includes(fc.fromNodeId)
        && !toBeDeleted.includes(fc.toNodeId)
    ),
    initiateStepCardVisible: stepItems.length === 0,
    isRootExternal: state.flowItems.length === 0 ? false : state.isRootExternal, // will show start button if canvas is completely empty
    showNotifyDialog: false,
    stepCardMenuDropdown: {
      ...state.stepCardMenuDropdown,
      xCoordinate: 0,
      yCoordinate: 0,
      isVisible: false
    },
    stepJourneyDataMap: {
      ...state.stepJourneyDataMap,
      [state.stepCardUUID]: {
        ...state.stepJourneyDataMap[state.stepCardUUID],
        flowItems,
        flowConnectors: state.flowConnectors,
        ingressNodeId: state.ingressNodeId,
        isRootExternal: state.isRootExternal,
        startingNode,
        maxExecutionTime: duration,
        journeyStepAction: [JourneyStepAction.NONE, JourneyStepAction.UPDATE].includes(state.stepJourneyDataMap[state.stepCardUUID].journeyStepAction) ? JourneyStepAction.UPDATE : JourneyStepAction.CREATE
      }
    },
    updateVersion: false,
    saveJourneyBtnEnabled: false,
    enableActiveJourney: state.journeyStatus.toLowerCase() === 'draft',
    maxExecutionTimeInput: calculatedDuration,
    selectedFlowItem: undefined,
    filteredServices: undefined,
    searchTerm: '',
    sidebarState: SidebarState.NONE,
    startSelected: false,
    designingStep: false,
    journeyStepName: '',
    journeyStepDescription: '',
    stepCardUUID: '',
    flowItems: [],
    flowConnectors: [],
  };
};

export const editStepDetails = (state: JourneyCanvasState): JourneyCanvasState => {
  if (!state.deletingNodeId) {
    return state;
  }
  // deletingNodeId is the step node id for which the dropdown is opened
  const step = state.stepJourneyDataMap[state.deletingNodeId];
  const prevNodeId = state.stepConnectors.find((c) => c.toNodeId === state.deletingNodeId)?.fromNodeId;
  const dropdownValues: { value: string; name: string; }[] = [];
  if (prevNodeId) {
    const lastElementIdf = state.stepJourneyDataMap[prevNodeId].flowItems.filter((fi) => !state.stepJourneyDataMap[prevNodeId].flowConnectors.find((fc) => fc.fromNodeId === fi.nodeId));
    lastElementIdf.forEach((e) => {
      dropdownValues.push({
        value: e.nodeId,
        name: e.name
      });
    });
  }
  return {
    ...state,
    showJourneyStepModal: true,
    journeyStepName: step.journeyStepName,
    journeyStepDescription: step.journeyStepDescription,
    maxExecutionTimeInput: {
      hours: Math.floor(step.maxExecutionTime / 3600).toString(),
      minutes: Math.floor((step.maxExecutionTime % 3600) / 60).toString(),
      seconds: (step.maxExecutionTime % 60).toString()
    },
    uniqueIdentifiers: step.uniqueIdentifiers,
    dropdownEndpointValues: dropdownValues,
    selectedEndpointFlowItem: dropdownValues.length > 0 ? step.startingNode : undefined,
    stepCardMenuDropdown: {
      ...state.stepCardMenuDropdown,
      isVisible: false,
    }
  };
};
